import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { IMAGES, CONTACT } from "../lib/data";
import { createContact } from "../lib/api";

const inputClass = "border-white/10 bg-matte-deep text-white placeholder:text-white/30 focus-visible:ring-gold h-12 rounded-none";
const labelClass = "font-body text-xs uppercase tracking-[0.15em] text-white/60";

export default function Contacts() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t("legal.consentReq"));
      return;
    }
    setSubmitting(true);
    try {
      await createContact({ ...form, lang });
      toast.success(t("contacts.sent"));
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch {
      toast.error("Error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const cards = [
    { icon: Phone, label: t("contacts.phone"), value: CONTACT.phoneIntl, href: `tel:${CONTACT.phone}`, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: CONTACT.phoneIntl, href: `https://wa.me/${CONTACT.whatsapp}`, testid: "contact-whatsapp" },
    { icon: Mail, label: t("contacts.writeUs"), value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  ];

  return (
    <>
      <PageHero tag={t("contacts.tag")} title={t("contacts.title")} subtitle={t("contacts.subtitle")} image={IMAGES.sorrento} />

      <section className="mx-auto max-w-6xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <div className="grid gap-5 sm:grid-cols-1">
              {cards.map((c, i) => (
                <Reveal key={c.testid} delay={i * 0.08}>
                  <a data-testid={c.testid} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                    className="group flex items-center gap-5 border border-white/10 bg-matte-surface p-6 transition-colors hover:border-gold/40">
                    <span className="flex h-12 w-12 items-center justify-center border border-gold/30 text-gold"><c.icon size={22} /></span>
                    <span>
                      <span className="block font-body text-[11px] uppercase tracking-[0.2em] text-white/40">{c.label}</span>
                      <span className="mt-1 block font-display text-lg text-white">{c.value}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
              <Reveal delay={0.24}>
                <div className="flex items-start gap-5 border border-white/10 bg-matte-surface p-6">
                  <span className="flex h-12 w-12 items-center justify-center border border-gold/30 text-gold"><MapPin size={22} /></span>
                  <span>
                    <span className="block font-body text-[11px] uppercase tracking-[0.2em] text-white/40">{t("contacts.area")}</span>
                    <span className="mt-1 block font-body text-sm text-white/70">{t("contacts.areaText")}</span>
                  </span>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-6 border border-white/10 bg-matte-surface p-8 lg:p-10">
              <h3 className="font-heading text-3xl text-white">{t("contacts.formTitle")}</h3>
              <div className="flex flex-col gap-2">
                <Label className={labelClass}>{t("booking.name")}</Label>
                <Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder={t("contacts.namePh")} required className={inputClass} />
              </div>
              <div className="flex flex-col gap-2">
                <Label className={labelClass}>{t("booking.email")}</Label>
                <Input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder={t("contacts.emailPh")} required className={inputClass} />
              </div>
              <div className="flex flex-col gap-2">
                <Label className={labelClass}>{t("booking.phone")}</Label>
                <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder={t("contacts.phonePh")} className={inputClass} />
              </div>
              <div className="flex flex-col gap-2">
                <Label className={labelClass}>{t("booking.message")}</Label>
                <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder={t("contacts.msgPh")} required className={`${inputClass} h-auto min-h-[130px] py-3`} />
              </div>
              <label className="flex items-start gap-3">
                <Checkbox data-testid="contact-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:text-black" />
                <span className="font-body text-xs leading-relaxed text-white/60">
                  {t("legal.consent")}{" "}
                  <Link to="/privacy" target="_blank" className="text-gold underline underline-offset-2">{t("legal.privacyLink")}</Link>.
                </span>
              </label>
              <button type="submit" data-testid="contact-submit" disabled={submitting}
                className="flex w-full items-center justify-center gap-2 bg-gold py-4 font-body text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-muted disabled:opacity-60">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {t("contacts.send")}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
